/* Brokerages page — tour planner.
 *
 * Pivot from the original "find + scrape brokerages" workflow (now defunct,
 * since Realtor.ca is the realtor source). This page lets the user:
 *   1. Sync addresses for every brokerage_name in the realtors collection,
 *      using Google Places "Find Place from Query" (browser-side because the
 *      Maps key is HTTP-referrer restricted)
 *   2. Browse offices in a List or Map view
 *   3. Edit any brokerage's office_address (flips source to 'manual')
 *   4. Select offices, auto-optimize the route, and export to Google Maps
 *
 * The old scrape buttons are tucked into a collapsible "Legacy" section at
 * the bottom — kept around for non-Saskatoon expansion.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2,
  Search,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Route,
  X,
  List,
  Map as MapIcon,
  Sparkles,
  Users,
  Settings as SettingsIcon,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import BrokerageMap from "@/components/BrokerageMap";
import BrokerageRouteDialog from "@/components/BrokerageRouteDialog";
import BrokerageDetail from "@/components/BrokerageDetail";
import ConfirmDialog from "@/components/ConfirmDialog";
import BrokerageList from "@/components/brokerages/BrokerageList";
import StatCard from "@/components/brokerages/StatCard";
import EmptyState from "@/components/brokerages/EmptyState";
import EditAddressDialog from "@/components/brokerages/EditAddressDialog";
import DeleteBrokerageDialog from "@/components/brokerages/DeleteBrokerageDialog";
import { useJobs } from "@/contexts/JobsContext";
import useStickyState from "@/hooks/useStickyState";
import useBrokerageGeocode from "@/hooks/useBrokerageGeocode";
import { DEFAULT_LOCATION } from "@/lib/brokerageGeocode";
import { mapsAuthExplanation } from "@/lib/gmapsAuth";
import CreditEstimate from "@/components/billing/CreditEstimate";

const GMAPS_LIBRARIES = ["places"];

export default function Brokerages() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [brokerages, setBrokerages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sticky across in-tab navigation so bouncing between Realtors ↔
  // Brokerages ↔ Outreach doesn't nuke the last search the user typed.
  const [search, setSearch] = useStickyState("brokerages:search", "");
  const [viewMode, setViewMode] = useState("list");
  const [selected, setSelected] = useState(new Set());
  const [selectionOrder, setSelectionOrder] = useState([]);
  // Optimization state for the route dialog (mirrors OpenHouses)
  const [directionsResult, setDirectionsResult] = useState(null);
  const [optimizationState, setOptimizationState] = useState("none");
  const [routeDialog, setRouteDialog] = useState(false);
  // Sync progress
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // {current, total, name}
  // Inline edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editLocationId, setEditLocationId] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  // Locations (admin can reassign a brokerage's workspace)
  const [locations, setLocations] = useState([]);
  // Expanded "View realtors" rows
  const [expanded, setExpanded] = useState(new Set());
  const [realtorsByBrokerageId, setRealtorsByBrokerageId] = useState({});
  // Home address coords (for map start pin)
  const [homeCoords, setHomeCoords] = useState(null);
  // Legacy scrape section toggle
  const [showLegacy, setShowLegacy] = useState(false);
  // Detail sheet (overlay) for in-app browsing
  const [detailTarget, setDetailTarget] = useState(null);
  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // City filter
  const [cityFilter, setCityFilter] = useState("all");
  // Min realtor count filter
  const [minRealtors, setMinRealtors] = useState(0);
  const [minRealtorsFocused, setMinRealtorsFocused] = useState(false);
  // Targeted brokerage enrichment (Firecrawl)
  const [enrichPlan, setEnrichPlan] = useState(null); // dry-run result
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const { track: trackJob } = useJobs();
  // Load Maps JS so we can call PlacesService client-side for sync
  const { isLoaded: gmapsLoaded } = useJsApiLoader({
    id: "gmaps-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GMAPS_LIBRARIES,
  });
  // Hidden map element used as the PlacesService host
  const placesHostRef = useRef(null);

  const mergeBrokerage = useCallback((updated) => {
    setBrokerages((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
  }, []);
  const locationNameFor = useCallback(
    (b) => locations.find((l) => l.id === b.location_id)?.name || user?.location || DEFAULT_LOCATION,
    [locations, user?.location],
  );
  const { geocodeOne, busyId: geocodingId } = useBrokerageGeocode({
    gmapsLoaded,
    hostRef: placesHostRef,
    locationNameFor,
    onSaved: mergeBrokerage,
  });

  // ----- Loaders -----
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brokerages");
      setBrokerages(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load brokerages", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Locations list for the admin's workspace reassignment select
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/locations");
        setLocations(data.locations || []);
      } catch {
        // non-critical
      }
    })();
  }, [isAdmin]);

  // Geocode user.home_address via Nominatim (same approach as Open Houses)
  useEffect(() => {
    const addr = (user?.home_address || "").trim();
    if (!addr) {
      setHomeCoords(null);
      return;
    }
    const cacheKey = `homeCoords:${addr}`;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (cached && typeof cached.lat === "number") {
        setHomeCoords(cached);
        return;
      }
    } catch (_) {}
    const ctrl = new AbortController();
    fetch(
      "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
        encodeURIComponent(addr),
      { headers: { Accept: "application/json" }, signal: ctrl.signal },
    )
      .then((r) => r.json())
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0) {
          const c = { lat: parseFloat(rows[0].lat), lng: parseFloat(rows[0].lon) };
          if (!Number.isNaN(c.lat) && !Number.isNaN(c.lng)) {
            setHomeCoords(c);
            try {
              localStorage.setItem(cacheKey, JSON.stringify(c));
            } catch (_) {}
          }
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [user?.home_address]);

  // ----- City extraction helper -----
  const extractCity = useCallback((address) => {
    if (!address) return "";
    // Try to extract city from typical address formats like "123 Main St, Saskatoon, SK S7K 1A1"
    const parts = address.split(",").map((p) => p.trim());
    if (parts.length >= 2) {
      // Second-to-last part is often the city (before province/postal)
      const candidate = parts.length >= 3 ? parts[parts.length - 2] : parts[0];
      // Remove postal code and province abbreviation
      return candidate.replace(/\b[A-Z]{2}\s+[A-Z0-9]{3}\s*[A-Z0-9]{3}\b/i, "").replace(/\b(SK|AB|BC|MB|ON|QC|NB|NS|PE|NL|NT|NU|YT)\b/i, "").trim();
    }
    return "";
  }, []);

  // Build city list from all brokerages
  const cities = useMemo(() => {
    const citySet = new Set();
    brokerages.forEach((b) => {
      const city = b.city || extractCity(b.office_address);
      if (city) citySet.add(city);
    });
    return [...citySet].sort();
  }, [brokerages, extractCity]);

  // ----- Filtering -----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = brokerages;
    if (q) {
      rows = rows.filter(
        (b) =>
          (b.name || "").toLowerCase().includes(q) ||
          (b.office_address || "").toLowerCase().includes(q),
      );
    }
    // City filter
    if (cityFilter !== "all") {
      rows = rows.filter((b) => {
        const city = b.city || extractCity(b.office_address);
        return city.toLowerCase() === cityFilter.toLowerCase();
      });
    }
    // Min realtor count filter
    if (minRealtors > 0) {
      rows = rows.filter((b) => (b.realtor_count || 0) >= minRealtors);
    }
    // Sort: with-address first, then by name
    return [...rows].sort((a, b) => {
      const aOk = a.geocode_status === "ok" ? 0 : 1;
      const bOk = b.geocode_status === "ok" ? 0 : 1;
      if (aOk !== bOk) return aOk - bOk;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [brokerages, search, cityFilter, minRealtors, extractCity]);

  const mappedCount = useMemo(
    () => brokerages.filter((b) => b.geocode_status === "ok").length,
    [brokerages],
  );

  // ----- Selection -----
  const toggleSelection = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectionOrder((arr) => {
      if (arr.includes(id)) return arr.filter((x) => x !== id);
      return [...arr, id];
    });
    setDirectionsResult(null);
    setOptimizationState("none");
  };

  const clearSelection = () => {
    setSelected(new Set());
    setSelectionOrder([]);
    setDirectionsResult(null);
    setOptimizationState("none");
  };

  const reorderSelection = (fromIdx, toIdx) => {
    setSelectionOrder((arr) => {
      if (
        fromIdx < 0 ||
        toIdx < 0 ||
        fromIdx >= arr.length ||
        toIdx >= arr.length ||
        fromIdx === toIdx
      ) {
        return arr;
      }
      const next = [...arr];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
    setOptimizationState((s) => (s === "optimized" ? "manual" : s));
  };

  const applyOptimizedOrder = (orderedIds, directions) => {
    setSelectionOrder(orderedIds);
    setDirectionsResult(directions);
    setOptimizationState("optimized");
  };

  // ----- Delete brokerage -----
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/brokerages/${deleteTarget.id}`);
      toast.success(`Deleted "${deleteTarget.name}"`, {
        description: "Realtors have been unassigned — their profiles are safe.",
      });
      setBrokerages((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      setSelected((s) => {
        const next = new Set(s);
        next.delete(deleteTarget.id);
        return next;
      });
      setSelectionOrder((arr) => arr.filter((x) => x !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    } finally {
      setDeleteBusy(false);
    }
  };

  // ----- Detail sheet update handler -----
  const handleDetailUpdated = (updated) => {
    setBrokerages((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
    setDetailTarget(updated);
  };

  const openDetail = (brokerage) => {
    setDetailTarget(brokerage);
  };

  const toggleSelectAll = () => {
    if (selected.size > 0) {
      clearSelection();
    } else {
      // Select every mapped brokerage on the visible list
      const ids = filtered.filter((b) => b.geocode_status === "ok").map((b) => b.id);
      setSelected(new Set(ids));
      setSelectionOrder(ids);
      setDirectionsResult(null);
      setOptimizationState("none");
    }
  };

  // ----- Sync addresses (client-side Google lookup, persisted per row) -----
  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const handleSync = async () => {
    if (!gmapsLoaded) {
      toast.error("Google Maps not ready yet — try again in a moment.");
      return;
    }
    setSyncBusy(true);
    try {
      // 1. Ask backend to ensure docs exist + return the manifest
      const { data: manifest } = await api.post("/brokerages/sync-addresses");
      toast.success(
        `Brokerage docs OK · ${manifest.docs_created} created · ${manifest.realtor_counts_updated} counts refreshed`,
      );
      // Filter the manifest down to brokerages that look like real offices —
      // skip ones with zero realtors (those are stale "Find brokerages" search
      // entries from the previous workflow).
      const fullList = (manifest.need_places_lookup || [])
        .map((row) => brokerages.find((x) => x.id === row.id))
        .filter((b) => b && (b.realtor_count || 0) > 0);
      if (fullList.length === 0) {
        toast.info("Every brokerage with realtors already has an address.");
        await load();
        return;
      }
      let geocoded = 0;
      let failed = 0;
      for (let i = 0; i < fullList.length; i++) {
        const b = fullList[i];
        setSyncProgress({ current: i + 1, total: fullList.length, name: b.name });
        const r = await geocodeOne(b, { silent: true });
        if (r.fatal) {
          toast.error("Sync stopped — Google Maps is unavailable", {
            description: r.error === "MAPS_AUTH_FAILED" ? mapsAuthExplanation() : String(r.error),
            duration: 12000,
          });
          break;
        }
        if (r.saved) geocoded += 1;
        else failed += 1;
        // Polite spacing to stay under Places QPS limits
        await sleep(200);
      }
      setSyncProgress(null);
      toast.success(`Sync complete · ${geocoded} geocoded · ${failed} failed`);
      await load();
    } catch (e) {
      toast.error("Sync failed", { description: formatApiError(e) });
    } finally {
      setSyncBusy(false);
      setSyncProgress(null);
    }
  };

  // "Not on map — Fix here": one-click lookup; if Google can't find the
  // stored address, drop straight into the edit dialog so it can be fixed.
  const fixPin = async (b) => {
    const r = await geocodeOne(b);
    if (r.notFound) openEdit(b);
  };

  // ----- Targeted enrichment for brokerages with missing data -----
  // Dry-run first → ConfirmDialog with cost preview → launch async job.
  const planEnrichMissing = async () => {
    setEnrichBusy(true);
    try {
      const { data } = await api.post("/brokerage-enrich-missing", { dry_run: true });
      setEnrichPlan(data);
      setEnrichOpen(true);
    } catch (e) {
      toast.error("Cost preview failed", { description: formatApiError(e) });
    } finally {
      setEnrichBusy(false);
    }
  };

  const launchEnrichMissing = async () => {
    setEnrichBusy(true);
    try {
      const { data } = await api.post("/brokerage-enrich-missing", { dry_run: false });
      if (data.job_id) {
        trackJob(data.job_id, {
          type: "enrich_brokerages",
          label: `${data.candidate_count} brokerages`,
        });
      }
      toast.success("Brokerage enrichment started", {
        description: `${data.candidate_count} brokerages · est. ${data.estimated_credits} Firecrawl credits`,
      });
      setEnrichOpen(false);
      setEnrichPlan(null);
      // Refresh shortly so freshly-filled fields appear once the job completes.
      setTimeout(load, 2000);
    } catch (e) {
      toast.error("Launch failed", { description: formatApiError(e) });
    } finally {
      setEnrichBusy(false);
    }
  };

  // ----- Edit office address -----
  const openEdit = (b) => {
    setEditTarget(b);
    setEditName(b.name || "");
    setEditAddress(b.office_address || "");
    setEditLocationId(b.location_id || "");
  };
  const saveEdit = async () => {
    if (!editTarget) return;
    setEditBusy(true);
    try {
      const payload = {};
      if (editName && editName !== editTarget.name) payload.name = editName;
      if (editAddress !== (editTarget.office_address || "")) payload.office_address = editAddress;
      if (isAdmin && editLocationId && editLocationId !== editTarget.location_id) payload.location_id = editLocationId;
      const { data } = await api.patch(`/brokerages/${editTarget.id}`, payload);
      // Optimistic merge
      setBrokerages((rows) => rows.map((r) => (r.id === data.id ? data : r)));
      toast.success("Brokerage updated");
      setEditTarget(null);
      // A new address is "pending" until Google places it — do that now so
      // the row is routable immediately instead of waiting for a sync.
      if (payload.office_address) await geocodeOne(data);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setEditBusy(false);
    }
  };

  // ----- Expand realtors per brokerage -----
  const toggleExpand = async (b) => {
    const isOpen = expanded.has(b.id);
    setExpanded((s) => {
      const next = new Set(s);
      if (isOpen) next.delete(b.id);
      else next.add(b.id);
      return next;
    });
    if (!isOpen && !realtorsByBrokerageId[b.id]) {
      try {
        const { data } = await api.get("/realtors", {
          params: { brokerage_name: b.name, limit: 200 },
        });
        setRealtorsByBrokerageId((m) => ({ ...m, [b.id]: Array.isArray(data) ? data : [] }));
      } catch (e) {
        setRealtorsByBrokerageId((m) => ({ ...m, [b.id]: [] }));
      }
    }
  };

  // ----- Render -----
  return (
    <div className="space-y-4 p-4 sm:p-6 max-w-7xl mx-auto" data-testid="brokerages-page">
      {/* Hidden host for PlacesService */}
      <div ref={placesHostRef} style={{ display: "none" }} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight flex items-center gap-2 text-slate-900">
            <Building2 className="h-7 w-7" /> Brokerages
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Saskatoon brokerage offices for in-person tour planning. Sync addresses to
            populate the map, select offices, and export an optimized Google Maps route.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin ? (
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={syncBusy || !gmapsLoaded}
              className="min-h-[44px]"
              data-testid="brokerages-sync-button"
            >
              {syncBusy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {syncBusy ? "Syncing…" : "Sync addresses"}
            </Button>
          ) : null}
          {isAdmin ? (
            <Button
              variant="outline"
              onClick={planEnrichMissing}
              disabled={enrichBusy}
              className="min-h-[44px]"
              data-testid="brokerages-enrich-missing-button"
              title="Firecrawl-scrape brokerage websites to fill missing phone / address / parent network"
            >
              {enrichBusy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Enrich missing data
            </Button>
          ) : null}
        </div>
      </div>

      {/* Sync progress banner */}
      {syncProgress ? (
        <div
          className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"
          data-testid="brokerage-sync-progress"
        >
          Looking up addresses via Google Places ·{" "}
          <span className="tabular-nums font-semibold">
            {syncProgress.current}/{syncProgress.total}
          </span>{" "}
          · {syncProgress.name}
        </div>
      ) : null}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard label="Brokerages" value={brokerages.length} />
        <StatCard label="Mapped" value={mappedCount} accent="emerald" />
        <StatCard label="Realtors" value={brokerages.reduce((s, b) => s + (b.realtor_count || 0), 0)} />
        <StatCard label="Selected" value={selected.size} accent="amber" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div
          className="inline-flex rounded-md border border-slate-200 bg-white p-1 shadow-sm"
          role="tablist"
          data-testid="brokerages-view-toggle"
        >
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition min-h-[40px] ${
              viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="brokerages-view-list"
          >
            <List className="h-4 w-4" /> List
          </button>
          <button
            type="button"
            onClick={() => setViewMode("map")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition min-h-[40px] ${
              viewMode === "map" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="brokerages-view-map"
          >
            <MapIcon className="h-4 w-4" /> Map
          </button>
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or address…"
            className="pl-9 pr-9 min-h-[44px]"
            data-testid="brokerages-search-input"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
              aria-label="Clear search"
              data-testid="brokerages-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/* City filter */}
        {cities.length > 1 ? (
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="sm:w-48 min-h-[44px]" data-testid="brokerages-city-filter">
              <Filter className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
              <SelectValue placeholder="All cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        {/* Min realtors filter */}
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-slate-500 whitespace-nowrap flex items-center gap-1">
            <Users className="h-3.5 w-3.5" /> Min realtors
          </Label>
          <Input
            type="number"
            min={0}
            value={minRealtorsFocused && minRealtors === 0 ? "" : minRealtors}
            onFocus={() => setMinRealtorsFocused(true)}
            onBlur={() => setMinRealtorsFocused(false)}
            onChange={(e) => setMinRealtors(Math.max(0, parseInt(e.target.value, 10) || 0))}
            className="w-16 min-h-[44px] text-center tabular-nums"
            data-testid="brokerages-min-realtors"
          />
        </div>

        {viewMode === "list" && filtered.length > 0 ? (
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 px-2 py-1 rounded hover:bg-slate-100 cursor-pointer min-h-[40px]">
            <Checkbox
              checked={selected.size > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="brokerages-select-all"
            />
            Select all mapped
          </label>
        ) : null}
      </div>

      {/* Sticky route chip */}
      {selectionOrder.length > 0 ? (
        <div
          className="sticky top-[4rem] md:top-2 z-20 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-2 flex flex-wrap items-center gap-2 shadow-md w-full sm:w-auto sm:inline-flex"
          data-testid="brokerage-route-chip"
        >
          <Route className="h-4 w-4 text-emerald-700" />
          <span className="text-sm font-semibold text-emerald-900">
            Route: {selectionOrder.length} office{selectionOrder.length === 1 ? "" : "s"} selected
          </span>
          <Button
            onClick={() => setRouteDialog(true)}
            className="text-white h-9 min-h-[36px] rounded-full"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="brokerages-open-route-builder"
          >
            Build Route →
          </Button>
          <Button
            variant="ghost"
            onClick={clearSelection}
            className="h-9 min-h-[36px] rounded-full text-emerald-900 hover:bg-emerald-100"
            data-testid="brokerages-clear-route-button"
          >
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        </div>
      ) : null}

      {/* Body */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : brokerages.length === 0 ? (
        <EmptyState onSync={isAdmin ? handleSync : null} syncBusy={syncBusy} />
      ) : viewMode === "map" ? (
        <BrokerageMap
          brokerages={filtered}
          selectedIds={selected}
          onToggleSelection={toggleSelection}
          onAreaSelect={(ids) => {
            setSelected((prev) => {
              const next = new Set(prev);
              ids.forEach((id) => next.add(id));
              return next;
            });
            setSelectionOrder((prev) => {
              const newIds = ids.filter((id) => !prev.includes(id));
              return [...prev, ...newIds];
            });
            setDirectionsResult(null);
            setOptimizationState("none");
            toast.success(`Added ${ids.length} office${ids.length === 1 ? "" : "s"} to route`);
          }}
          homeCoords={homeCoords}
          directions={directionsResult}
          onShowRealtors={openDetail}
        />
      ) : (
        <BrokerageList
          rows={filtered}
          selectedIds={selected}
          onToggle={toggleSelection}
          onEdit={openEdit}
          onDetail={openDetail}
          onDelete={(b) => setDeleteTarget(b)}
          onFixPin={fixPin}
          fixingId={geocodingId}
          isAdmin={isAdmin}
          expanded={expanded}
          onExpand={toggleExpand}
          realtorsById={realtorsByBrokerageId}
          extractCity={extractCity}
        />
      )}

      {/* Route dialog */}
      <BrokerageRouteDialog
        open={routeDialog}
        onClose={() => setRouteDialog(false)}
        selectedIds={selectionOrder}
        brokerages={brokerages}
        onReorder={reorderSelection}
        onRemoveStop={(id) => toggleSelection(id)}
        optimizationState={optimizationState}
        onApplyOptimized={applyOptimizedOrder}
        homeCoords={homeCoords}
        onOpenDetail={openDetail}
      />

      {/* Edit brokerage dialog (name + address + location) */}
      <EditAddressDialog
        target={editTarget}
        name={editName}
        onNameChange={setEditName}
        address={editAddress}
        onAddressChange={setEditAddress}
        locationId={editLocationId}
        onLocationChange={setEditLocationId}
        locations={locations}
        isAdmin={isAdmin}
        busy={editBusy}
        onSave={saveEdit}
        onClose={() => setEditTarget(null)}
      />

      {/* Brokerage detail sheet (overlay) */}
      <Sheet open={!!detailTarget} onOpenChange={(v) => !v && setDetailTarget(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="brokerage-detail-sheet"
        >
          <SheetHeader>
            <SheetTitle className="font-display">Brokerage Details</SheetTitle>
          </SheetHeader>
          {detailTarget ? (
            <div className="mt-4">
              <BrokerageDetail
                brokerage={detailTarget}
                onUpdated={handleDetailUpdated}
              />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Delete confirm dialog */}
      <DeleteBrokerageDialog
        target={deleteTarget}
        busy={deleteBusy}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Legacy scrape section — hidden by default; admin can expand */}
      {isAdmin ? (
        <div className="rounded-md border border-slate-200 bg-slate-50">
          <button
            type="button"
            className="w-full px-4 py-2.5 text-left text-xs text-slate-500 hover:bg-slate-100 inline-flex items-center justify-between gap-2 min-h-[40px]"
            onClick={() => setShowLegacy((v) => !v)}
            data-testid="brokerages-legacy-toggle"
          >
            <span className="inline-flex items-center gap-1">
              <SettingsIcon className="h-3.5 w-3.5" />
              Legacy scrape tools {showLegacy ? "" : "(hidden)"}
            </span>
            {showLegacy ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
          {showLegacy ? (
            <div className="px-4 pb-4 space-y-2 text-xs text-slate-600">
              <p>
                These were the original "Find brokerages" and "Scrape realtors from a brokerage
                website" workflows used before Realtor.ca became the realtor source. They're not
                used for Saskatoon anymore but can be useful when expanding to a new market.
              </p>
              <p className="text-amber-700">
                Use the <Link to="/scrape-history" className="underline">Scrape History</Link> page
                to inspect any past runs, and the existing endpoints
                (<code className="bg-white px-1 py-0.5 rounded">/api/brokerages/find</code>,{" "}
                <code className="bg-white px-1 py-0.5 rounded">/api/brokerages/{`{id}`}/scrape-realtors</code>)
                still work if you call them directly.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={enrichOpen}
        onClose={() => setEnrichOpen(false)}
        onConfirm={launchEnrichMissing}
        title="Enrich brokerages with missing data?"
        description={
          enrichPlan ? (
            <div className="text-sm space-y-2">
              <p>
                Targets: <b>{enrichPlan.candidate_count}</b> brokerages missing an
                office address or phone number (and that have a website Firecrawl can scrape).
              </p>
              <p>
                Estimated Firecrawl credits: <b>{enrichPlan.estimated_credits}</b>
                {typeof enrichPlan.firecrawl_remaining === "number" ? (
                  <>
                    {" "}· remaining this month: <b>{enrichPlan.firecrawl_remaining}</b>
                  </>
                ) : null}
              </p>
              {enrichPlan.candidate_count > 0 ? (
                <CreditEstimate action="brokerage_enrich" params={{ count: enrichPlan.candidate_count }} />
              ) : null}
              {!enrichPlan.ok_to_proceed ? (
                <p className="text-red-700 text-xs">
                  Not enough Firecrawl credits remaining for this run. Raise the cap
                  in Settings → Scraping, or trim the list.
                </p>
              ) : (
                <p className="text-xs text-slate-500">
                  Firecrawl will visit each brokerage homepage and fill in only the
                  missing fields (phone, head-office address, parent franchise name).
                  Manually-edited data is never overwritten.
                </p>
              )}
            </div>
          ) : (
            ""
          )
        }
        confirmLabel="Run enrichment"
        confirmDisabled={!enrichPlan || !enrichPlan.ok_to_proceed}
        testid="brokerages-enrich-missing-confirm"
      />
    </div>
  );
}
